import { Link } from "@fluentui/react";
import { LinkRegular } from "@fluentui/react-icons";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { CRMEngagementAISummaryPanel } from "../../../helper/CRMEngagementAISummaryPanel";
import { formatDate } from "../../../helper/DateFormat";
import { ICRMCustomerProductInfoTableProps } from "../../../interfaces/crm-customer-product";
import { getDaysBetweenDates } from "../../../Utils/js-common";
import { CustomDataGrid } from "../../../SharedComponents/CustomDataGrid/CustomDataGrid";

export default function CRMCustomerProductInfoTable(props: ICRMCustomerProductInfoTableProps) {
  const navigate = useNavigate();
  const columns: GridColDef[] = [
    {
      headerName: "CRM Link",
      field: "crmLink",
      width: 100,
      sortable: true,
      renderCell: (params) => (
        <Link
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            fontSize: 26,
            cursor: "pointer"
          }}
          href={params.row.crmLink}
          title={params.row.crmLink}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={params.row.tabIndex}
        >
          <LinkRegular />
        </Link>
      )
    },
    {
      headerName: "AI Summary",
      field: "aiSummary",
      width: 100,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <CRMEngagementAISummaryPanel
          customerProductId={params.row.customerProductId}
          isAISummaryPresent={params.row.isAISummaryPresent}
          pageName="Product View Page - Customer Engagements Tab"
        />
      )
    },
    {
      headerName: "Tenant Name",
      field: "tenantName",
      width: 290,
      sortable: true,
      renderCell: ({ row: { tenantId, tenantName } }: any) => (
        <a
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          href="#"
          onClick={() => {
            navigate("/customer?id=" + tenantId + "&from=productview");
          }}
          title={tenantName}
        >
          {tenantName}
        </a>
      )
    },
    {
      headerName: "Notes",
      field: "notes",
      width: 300,
      sortable: true
    },
    {
      headerName: "Modified Notes On",
      type: "date",
      field: "lastNotesModifiedOn",
      width: 180,
      sortable: true,
      valueGetter: (value, row) => new Date(row.lastNotesModifiedOn),
      renderCell: (params) => formatDate(params.row.lastNotesModifiedOn)
    },
    {
      headerName: "Owner",
      field: "owner",
      width: 220,
      sortable: true
    },
    {
      headerName: "PM List",
      field: "pmList",
      width: 220,
      sortable: true
    },
    {
      type: "number",
      headerName: "Idle Days",
      field: "idleDays",
      width: 100,
      sortable: true,
      valueGetter: (value, row) => getIdleDays(row.lastNotesModifiedOn)
    },
    {
      headerName: "Sales Stage",
      field: "salesStage",
      width: 120,
      sortable: true
    },
    {
      headerName: "Next Steps",
      field: "nextSteps",
      width: 220,
      sortable: true
    },
    {
      headerName: "Created On",
      type: "date",
      field: "notesCreatedOn",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => new Date(row.notesCreatedOn),
      renderCell: (params) => formatDate(params.row.notesCreatedOn)
    },
    {
      headerName: "Modified On",
      type: "date",
      field: "notesModifiedOn",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => new Date(row.notesModifiedOn),
      renderCell: (params) => formatDate(params.row.notesModifiedOn)
    },
    {
      headerName: "Subject",
      field: "subject",
      width: 270,
      sortable: true
    },
    {
      headerName: "Deployment Type",
      field: "deploymentType",
      width: 270,
      sortable: true
    },
    {
      headerName: "Deployment",
      field: "deployment",
      width: 120,
      sortable: true
    },
    {
      headerName: "Deployment Status",
      field: "deploymentStatus",
      width: 270,
      sortable: true
    },
    {
      headerName: "State",
      field: "customerProductState",
      width: 100,
      sortable: true
    },
    {
      headerName: "Owner Type",
      field: "ownerType",
      width: 100,
      sortable: true
    },
    {
      headerName: "Eng Owner",
      field: "engOwner",
      width: 220,
      sortable: true
    },
    {
      headerName: "Engagement Owner",
      field: "engagementOwner",
      width: 220,
      sortable: true
    },
    {
      headerName: "Pre-Sales Engaged",
      field: "preSalesEngaged",
      width: 150,
      sortable: true
    },
    {
      headerName: "CAT Engaged",
      field: "catEngaged",
      width: 120,
      sortable: true
    },
    {
      headerName: "partner Engaged",
      field: "partnerEngaged",
      width: 120,
      sortable: true
    },
    {
      headerName: "Weekly Tweet",
      field: "weeklyTweet",
      width: 270,
      sortable: true
    },
    {
      headerName: "Next Contact Date",
      type: "date",
      field: "nextContactDate",
      width: 130,
      sortable: true,
      valueGetter: (value, row) => new Date(row.nextContactDate),
      renderCell: (params) => formatDate(params.row.nextContactDate)
    },
    {
      headerName: "Customer Product Id",
      field: "customerProductId",
      width: 290,
      sortable: true
    }
  ];

  const showNoData = () => {
    const rootStyle = {
      display: "flex",
      justifyContent: "center",
      alightItems: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`CRM Customer Product details are not available for ${props.productName}`}</div>;
  };

  const getIdleDays = (lastNotesModifiedOn: any) => {
    const currentDate = new Date();
    const modifiedDate = new Date(lastNotesModifiedOn ?? currentDate);
    modifiedDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    return getDaysBetweenDates(currentDate, modifiedDate);
  };

  return props?.tableData && props?.tableData.length > 0 ? (
    <div style={{ height: 600, marginLeft: 8, marginRight: 8 }}>
      <CustomDataGrid
        columns={columns}
        getRowId={(row) => row.customerProductId}
        rows={{ values: props.tableData ?? [], count: props.tableData?.length ?? 0 }}
        paginationOptions={{ page: 0, pageSize: 25 }}
        kustoLink={props.crmDataKustoLink}
        TabName={`Product View - Customer Engagements Table for ${props.productName}`}
      />
    </div>
  ) : (
    showNoData()
  );
}
